import { inject, Service } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ToastService } from '@app/core/services/toast.service';
import { DialogService } from '@app/core/services/dialog.service';
import { BaseService } from '@app/core/services/base.service';
import { CertificateDto, EmployeeDto, LoggingDto } from '@app/_api/dto';
import { AccountStatusEnum } from '@app/_api/enums/account-status.enum';

@Service()
export class AdminApi extends BaseService {
  constructor() {
    const http = inject(HttpClient);
    const toastService = inject(ToastService);
    const dialog = inject(DialogService);

    super(http, '/admin', toastService, dialog);
  }

  isUserDirectoryConnected(): Observable<boolean> {
    return this.handleHttp(this.http.get<boolean>(this.buildPath('/user-directory/connected')));
  }

  blockEmployee(employee: EmployeeDto) {
    return this.handleHttp(this.http.post<AccountStatusEnum>(this.buildPath(`/block/${employee.uuid}`), null), {
      success: 'Benutzer erfolgreich gesperrt',
      error: 'Fehler beim Sperren des Benutzers',
    });
  }

  unblockEmployee(employee: EmployeeDto) {
    return this.handleHttp(this.http.post<AccountStatusEnum>(this.buildPath(`/unblock/${employee.uuid}`), null), {
      success: 'Benutzer erfolgreich freigeschaltet',
      error: 'Fehler beim Freischalten des Benutzers',
    });
  }

  updateEmployee(employee: EmployeeDto): Observable<EmployeeDto> {
    return this.handleHttp<EmployeeDto>(
      this.http.post<EmployeeDto>(this.buildPath(`/update/${employee.uuid}`), JSON.stringify(employee.role), {
        headers: { 'Content-Type': 'application/json' },
      }),
      {
        success: 'Benutzer erfolgreich aktualisiert',
        error: 'Fehler beim Aktualisieren des Benutzers',
      },
    );
  }

  deleteEmployee(uuid: string): Observable<EmployeeDto> {
    return this.handleHttp(this.http.delete<EmployeeDto>(this.buildPath(`/employee/${uuid}`)), {
      success: 'Benutzer erfolgreich gelöscht',
      error: 'Fehler beim Benutzer löschen',
    });
  }

  getAllLoggings() {
    return this.handleHttp(this.http.get<LoggingDto[]>(this.buildPath('/loggings/all')));
  }

  sendPersonalReminderMails(uuids: string[]) {
    return this.handleHttp(this.http.post<string[]>(this.buildPath('/mail/send'), uuids), {
      success: 'Mails erfolgreich versendet',
      error: 'Fehler beim Versenden der Mails',
    });
  }

  notifyUsersAboutVisibility(uuids: string[]) {
    return this.handleHttp<void>(this.http.patch<void>(this.buildPath('/notification'), uuids), {
      success: 'Nutzer erfolgreich benachrichtigt',
      error: 'Fehler beim Benachrichtigen der Nutzer',
    });
  }

  updateCertificate(certificate: CertificateDto) {
    return this.handleHttp(
      this.http.patch<CertificateDto>(this.buildPath(`/certificate/update/${certificate.uuid}`), {
        title: certificate.title,
        issuer: certificate.issuer,
      }),
      {
        success: 'Zertifikat erfolgreich aktualisiert',
        error: 'Fehler beim Aktualisieren des Zertifikats',
      },
    );
  }

  mergeCertificates(source: CertificateDto, target: CertificateDto) {
    return this.handleHttp(this.http.patch<CertificateDto>(this.buildPath(`/certificates/merge/${source.uuid}/${target.uuid}`), {}), {
      success: 'Zertifikat erfolgreich fusioniert',
      error: 'Fehler beim Zertifikat Fusionieren',
    });
  }

  deleteCertificate(certificate: CertificateDto) {
    return this.handleHttp<string>(this.http.delete<string>(this.buildPath(`/certificate/${certificate.uuid}`)), {
      success: 'Zertifikat erfolgreich gelöscht',
      error: 'Fehler beim Löschen des Zertifikats',
    });
  }
}
